/* === SERENE HEAT — predictive search ===
 * Debounced (250ms) fetch from /search/suggest.json
 * Keyboard nav (arrow up/down, enter, escape)
 * Mobile: input expands full-width when focused (handled in CSS)
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 250;
  var MIN_QUERY_LEN = 2;
  var MAX_PRODUCTS = 4;
  var MAX_ARTICLES = 3;

  function moneyFormat(cents) {
    if (typeof cents !== 'number' && typeof cents !== 'string') return '';
    var n = parseFloat(cents);
    if (isNaN(n)) return '';
    // Shopify suggest.json returns a "price" string already formatted in most themes,
    // but in some it's cents. We accept either.
    if (n > 0 && n < 10000 && (typeof cents === 'string' && cents.indexOf('$') === 0)) return cents;
    return '$' + (n / 100).toFixed(2).replace(/\.00$/, '');
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  function init(root) {
    if (!root || root.dataset.sereneSearchInited === '1') return;
    root.dataset.sereneSearchInited = '1';

    var toggle = root.querySelector('[data-serene-search-toggle]');
    var panel = root.querySelector('[data-serene-search-panel]');
    var input = root.querySelector('[data-serene-search-input]');
    var form = root.querySelector('[data-serene-search-form]');
    var results = root.querySelector('[data-serene-search-results]');
    var closeBtn = root.querySelector('[data-serene-search-close]');

    if (!toggle || !panel || !input || !results) return;

    var open = false;
    var activeIndex = -1;
    var items = [];
    var lastQuery = '';
    var abortCtrl = null;

    function setOpen(state) {
      open = state;
      panel.setAttribute('aria-hidden', state ? 'false' : 'true');
      toggle.setAttribute('aria-expanded', state ? 'true' : 'false');
      panel.classList.toggle('is-open', state);
      root.classList.toggle('is-open', state);
      document.body.classList.toggle('serene-search-open', state);
      if (state) {
        setTimeout(function () { input.focus(); }, 30);
      } else {
        results.hidden = true;
        results.innerHTML = '';
        activeIndex = -1;
      }
    }

    function buildResults(data, q) {
      var products = (data.resources && data.resources.results && data.resources.results.products) || [];
      var articles = (data.resources && data.resources.results && data.resources.results.articles) || [];
      products = products.slice(0, MAX_PRODUCTS);
      articles = articles.slice(0, MAX_ARTICLES);

      var html = '';

      if (products.length === 0 && articles.length === 0) {
        html = '<div class="serene-search__empty">'
          + '<p class="serene-search__empty-title">No matches for &ldquo;' + escapeHtml(q) + '&rdquo;</p>'
          + '<p class="serene-search__empty-sub">Try the HeatPulse Belt or browse our journal.</p>'
          + '<div class="serene-search__empty-links">'
          + '<a href="/products/heatpulse-belt">Shop HeatPulse</a>'
          + '<a href="/blogs/journal">Read the journal</a>'
          + '</div></div>';
        results.innerHTML = html;
        results.hidden = false;
        items = [];
        activeIndex = -1;
        return;
      }

      if (products.length) {
        html += '<div class="serene-search__group">'
          + '<h4 class="serene-search__group-title">Products</h4>'
          + '<ul class="serene-search__list">';
        products.forEach(function (p) {
          var img = p.featured_image && p.featured_image.url ? p.featured_image.url : (p.image || '');
          var price = p.price || '';
          html += '<li class="serene-search__item is-product">'
            + '<a href="' + escapeHtml(p.url) + '" data-search-link>'
            + (img ? '<span class="serene-search__thumb"><img src="' + escapeHtml(img) + '" alt="" loading="lazy" width="56" height="56"></span>' : '<span class="serene-search__thumb"></span>')
            + '<span class="serene-search__meta">'
            + '<span class="serene-search__title">' + escapeHtml(p.title) + '</span>'
            + (price ? '<span class="serene-search__price">' + escapeHtml(price) + '</span>' : '')
            + '</span></a></li>';
        });
        html += '</ul></div>';
      }

      if (articles.length) {
        html += '<div class="serene-search__group">'
          + '<h4 class="serene-search__group-title">Journal</h4>'
          + '<ul class="serene-search__list">';
        articles.forEach(function (a) {
          html += '<li class="serene-search__item is-article">'
            + '<a href="' + escapeHtml(a.url) + '" data-search-link>'
            + '<span class="serene-search__article-icon" aria-hidden="true">' + '&#9998;' + '</span>'
            + '<span class="serene-search__meta">'
            + '<span class="serene-search__title">' + escapeHtml(a.title) + '</span>'
            + (a.summary ? '<span class="serene-search__sub">' + escapeHtml(a.summary).slice(0, 110) + '</span>' : '')
            + '</span></a></li>';
        });
        html += '</ul></div>';
      }

      html += '<a href="/search?q=' + encodeURIComponent(q) + '" class="serene-search__viewall" data-search-link>View all results &rarr;</a>';

      results.innerHTML = html;
      results.hidden = false;
      items = Array.prototype.slice.call(results.querySelectorAll('[data-search-link]'));
      activeIndex = -1;
    }

    function renderLoading() {
      results.innerHTML = '<div class="serene-search__loading">Searching&hellip;</div>';
      results.hidden = false;
    }

    function fetchResults(q) {
      if (abortCtrl) abortCtrl.abort();
      abortCtrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;

      var url = '/search/suggest.json?q=' + encodeURIComponent(q)
        + '&resources[type]=product,article'
        + '&resources[limit]=' + MAX_PRODUCTS
        + '&resources[options][fields]=title,product_type,variants.title,vendor';

      var opts = abortCtrl ? { signal: abortCtrl.signal } : {};

      fetch(url, opts)
        .then(function (r) { return r.json(); })
        .then(function (data) { buildResults(data, q); })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          results.innerHTML = '<div class="serene-search__empty"><p class="serene-search__empty-title">Search unavailable.</p><p class="serene-search__empty-sub">Try again in a moment.</p></div>';
          results.hidden = false;
        });
    }

    var doSearch = debounce(function () {
      var q = input.value.trim();
      if (q.length < MIN_QUERY_LEN) {
        results.hidden = true;
        results.innerHTML = '';
        items = [];
        activeIndex = -1;
        return;
      }
      if (q === lastQuery) return;
      lastQuery = q;
      renderLoading();
      fetchResults(q);
    }, DEBOUNCE_MS);

    function setActive(idx) {
      if (!items.length) return;
      if (idx < 0) idx = items.length - 1;
      if (idx >= items.length) idx = 0;
      items.forEach(function (el, i) {
        el.classList.toggle('is-active', i === idx);
        if (i === idx) el.setAttribute('aria-selected', 'true');
        else el.removeAttribute('aria-selected');
      });
      activeIndex = idx;
      var li = items[idx].closest('li');
      if (li && li.scrollIntoView) li.scrollIntoView({ block: 'nearest' });
    }

    toggle.addEventListener('click', function () { setOpen(!open); });
    if (closeBtn) closeBtn.addEventListener('click', function () { setOpen(false); });

    input.addEventListener('input', doSearch);

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { setOpen(false); toggle.focus(); return; }
      if (!items.length) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(activeIndex + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(activeIndex - 1); }
      else if (e.key === 'Enter') {
        if (activeIndex >= 0 && items[activeIndex]) {
          e.preventDefault();
          window.location.href = items[activeIndex].getAttribute('href');
        }
      }
    });

    // Click outside to close
    document.addEventListener('click', function (e) {
      if (!open) return;
      if (root.contains(e.target)) return;
      setOpen(false);
    });

    // Submit form: native /search behaviour is fine
    if (form) {
      form.addEventListener('submit', function () {
        // allow native nav to /search?q=...
      });
    }
  }

  function bootstrap() {
    document.querySelectorAll('[data-serene-search]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
